import { Button, Flex, theme } from 'antd'
import {
  Bot,
  Briefcase,
  CalendarDays,
  MoreHorizontal,
  Newspaper,
  Search,
  Star,
  UserPlus,
  Video,
  type LucideIcon
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { AttributionSourceEnum as AttributionKey } from 'src/api/main'

import { ATTRIBUTIONS } from '../constants'
import { useStyles } from '../styles'
import type { OnboardingState } from '../useOnboardingState'

import { StepHeader } from './StepHeader'

type Props = { state: OnboardingState }

/**
 * One monochrome glyph per channel, all from the same set. No brand marks:
 * Lucide dropped its own, this repo carries no LinkedIn or YouTube asset, and a
 * single coloured logo among plain glyphs reads as the odd one out. Each icon
 * says what the channel *is* rather than standing in for a logo.
 */
const ATTRIBUTION_ICONS: Record<AttributionKey, LucideIcon> = {
  google: Search,
  youtube: Video,
  linkedin: Briefcase,
  reviews: Star,
  referral: UserPlus,
  ai_assistant: Bot,
  event: CalendarDays,
  press: Newspaper,
  other: MoreHorizontal
}

/**
 * Attribution — where the account came from.
 *
 * Optional, and the only answer the profile takes by a PATCH of its own: the
 * platform treats `industry` as write-once, so `source` cannot ride along with
 * a question this screen may one day be asked after. Nothing downstream depends
 * on it, hence the Skip action in the footer.
 */
export const AttributionStep = ({ state }: Props) => {
  const { t } = useTranslation()
  const { styles, cx } = useStyles()
  const { token } = theme.useToken()
  const { draft, patch } = state

  return (
    <Flex vertical gap={16}>
      <StepHeader title={t('workspaces.onboarding.attribution.title')} />

      <div className={styles.tileRow}>
        {ATTRIBUTIONS.map((source) => {
          const selected = draft.attribution === source
          const Icon = ATTRIBUTION_ICONS[source]

          return (
            <Button
              key={source}
              className={cx(styles.tile, selected && styles.tileSelected)}
              icon={
                <Icon
                  size={16}
                  color={
                    selected ? token.colorPrimaryText : token.colorTextTertiary
                  }
                />
              }
              onClick={() => patch({ attribution: source })}
            >
              {t(`workspaces.onboarding.attribution.options.${source}`)}
            </Button>
          )
        })}
      </div>
    </Flex>
  )
}
